/** SUNTRONIX ONE — Commercial Quotations Dashboard.
 *  One page.  KPIs + facet filters + rich list + Quote 360 drawer.
 *  Suntronix-internal RBAC + strict tenant scope on the backend.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  FileText, RefreshCw, CloudUpload, X, Search, Filter,
  CheckCircle2, AlertTriangle, XCircle, ArrowLeft,
} from "lucide-react";


const STATUS_TONE = {
  DRAFT: "bg-slate-100 text-slate-700 border-slate-200",
  SENT: "bg-sky-50 text-sky-800 border-sky-200",
  INTERNAL_REVIEW: "bg-amber-50 text-amber-800 border-amber-200",
  ACCEPTED: "bg-emerald-50 text-emerald-800 border-emerald-200",
  REJECTED: "bg-rose-50 text-rose-800 border-rose-200",
  EXPIRED: "bg-slate-100 text-slate-500 border-slate-200",
};

const SYNC_TONE = {
  SYNCED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "UPDATE AVAILABLE": "bg-amber-50 text-amber-800 border-amber-200",
  "REVIEW REQUIRED": "bg-violet-50 text-violet-800 border-violet-200",
  ERROR: "bg-rose-50 text-rose-800 border-rose-200",
};


export default function QuotationsDashboard() {
  const [f, setF] = useState({
    customer: "", status: "", assigned_user: "", currency: "",
    source: "", brand: "", date_from: "", date_to: "", q: "",
  });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [views, setViews] = useState([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [newViewName, setNewViewName] = useState("");
  const [newViewShared, setNewViewShared] = useState(false);

  const loadViews = useCallback(async () => {
    try { const r = await api.get("/commercial/quotations/saved-views");
      setViews(r.data);
    } catch (e) { /* silent */ }
  }, []);
  useEffect(() => { loadViews(); }, [loadViews]);

  const saveView = async () => {
    if (!newViewName.trim()) return;
    try {
      await api.post("/commercial/quotations/saved-views",
        { name: newViewName.trim(), filters: f, is_shared: newViewShared });
      toast.success(`Saved view · ${newViewName}`);
      setShowSaveDialog(false);
      setNewViewName(""); setNewViewShared(false);
      await loadViews();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const deleteView = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/commercial/quotations/saved-views/${id}`);
      toast.success("View removed");
      await loadViews();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const applyView = (v) => { setPage(1); setF(v.filters || {}); toast.info(`Applied · ${v.name}`); };

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = { page, page_size: 25 };
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/commercial/quotations/dashboard", { params });
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setBusy(false); }
  }, [f, page]);
  useEffect(() => { load(); }, [load]);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/commercial/quotations/vtiger/sync");
      toast.success(`Synced ${data.stats.read} · Created ${data.stats.created} · Updated ${data.stats.updated}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); }
  };

  const fmt = (n) => n == null ? "—"
    : Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 });
  const hasFilters = useMemo(() => Object.values(f).some(v => v), [f]);
  const clearFilters = () => setF({ customer: "", status: "", assigned_user: "",
    currency: "", source: "", brand: "", date_from: "", date_to: "", q: "" });

  return (
    <div className="space-y-5 max-w-[1600px] p-4" data-testid="quotations-dashboard">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[.16em] text-slate-500">Commercial</div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 mt-1 flex items-center gap-2">
            <FileText className="h-6 w-6 text-emerald-700" /> Sales &amp; Quotations
          </h1>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">
            All quotations in the pipeline. Quoted values are shown as pipeline
            volume — never labelled as revenue.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} disabled={busy}
            data-testid="dash-refresh">
            <RefreshCw className={"h-3.5 w-3.5 mr-1.5 " + (busy ? "animate-spin" : "")} />
            Refresh
          </Button>
          <Button onClick={syncNow} disabled={syncing}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="dash-sync-now">
            <CloudUpload className={"h-3.5 w-3.5 mr-1.5 " + (syncing ? "animate-pulse" : "")} />
            {syncing ? "Syncing…" : "SYNC NOW"}
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="kpi-row">
        <Kpi label="Total Quotations" value={data?.kpis?.TOTAL} />
        <Kpi label="Draft"                     value={data?.kpis?.DRAFT} />
        <Kpi label="Submitted"                 value={data?.kpis?.SUBMITTED} tone="sky" />
        <Kpi label="Accepted"                 value={data?.kpis?.ACCEPTED} tone="ok" />
        <Kpi label="Rejected"                 value={data?.kpis?.REJECTED} tone="err" />
        <Kpi label="Expired"                   value={data?.kpis?.EXPIRED} tone="warn" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <Kpi label="Total Quoted Value (pipeline volume)"
          value={fmt(data?.values?.TOTAL_QUOTED)} large />
        <Kpi label="Accepted Quoted Value (pipeline volume)"
          value={fmt(data?.values?.ACCEPTED_QUOTED)} large tone="ok" />
      </div>

      {/* Sync health */}
      {data?.sync?.last_sync && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="sync-row">
          <Kpi label="Last Successful Sync"
            value={new Date(data.sync.last_sync.run_at).toLocaleString()} tiny />
          <Kpi label="Records Synchronized" value={data.sync.counts.SYNCED} tone="ok" tiny />
          <Kpi label="Records Requiring Review"
            value={data.sync.counts["REVIEW REQUIRED"] + data.sync.counts["UPDATE AVAILABLE"]}
            tone="warn" tiny />
          <Kpi label="Sync Status" value={data.sync.last_sync.sync_status}
            tone={data.sync.last_sync.sync_status === "PASS" ? "ok" : "err"} tiny />
        </div>
      )}

      {/* Saved views chip row */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 flex items-center flex-wrap gap-2"
        data-testid="saved-views-bar">
        <div className="text-[11px] font-semibold text-slate-700 mr-2">Saved views:</div>
        {views.length === 0 && (
          <div className="text-[11px] text-slate-400 italic">
            No saved views yet — set filters, then click Save view.
          </div>
        )}
        {views.map(v => (
          <div key={v.id} role="button" tabIndex={0} onClick={() => applyView(v)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); applyView(v); } }}
            data-testid={`view-chip-${v.id}`}
            className="group inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-[11px] text-emerald-800 hover:bg-emerald-100 cursor-pointer select-none">
            <span>{v.name}</span>
            {v.is_shared && <Badge className="text-[9px] bg-white border-emerald-300 text-emerald-800">shared</Badge>}
            {v.is_mine && (
              <button type="button" onClick={(e) => deleteView(v.id, e)}
                className="opacity-40 hover:opacity-100 hover:text-rose-700"
                data-testid={`view-delete-${v.id}`}>
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        ))}
        {hasFilters && (
          <button onClick={() => setShowSaveDialog(true)}
            data-testid="save-view-btn"
            className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-slate-300 text-[11px] text-slate-700 hover:bg-slate-50">
            + Save current filters as view
          </button>
        )}
      </div>

      {showSaveDialog && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
          data-testid="save-view-dialog" onClick={() => setShowSaveDialog(false)}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-slate-200 text-sm font-heading font-semibold">Save current filters as a view</div>
            <div className="p-4 space-y-3">
              <label className="block">
                <div className="text-[11px] text-slate-600 mb-1">View name</div>
                <Input value={newViewName} onChange={e => setNewViewName(e.target.value)}
                  placeholder="e.g. My open quotes above 10L expiring this month"
                  data-testid="save-view-name" autoFocus />
              </label>
              <label className="flex items-center gap-2 text-[12px] text-slate-700">
                <input type="checkbox" checked={newViewShared}
                  onChange={e => setNewViewShared(e.target.checked)}
                  data-testid="save-view-shared" />
                Share with the team (others can apply, only I can delete)
              </label>
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowSaveDialog(false)}>Cancel</Button>
              <Button onClick={saveView} disabled={!newViewName.trim()}
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid="save-view-save">Save view</Button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-3"
        data-testid="filter-bar">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-3.5 w-3.5 text-slate-500" />
          <div className="text-[12px] font-semibold text-slate-700">Filters</div>
          {hasFilters && (
            <Button size="sm" variant="ghost" onClick={clearFilters}
              className="h-6 text-[11px]" data-testid="clear-filters">
              <X className="h-3 w-3 mr-1" /> Clear all
            </Button>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 text-[12px]">
          <FSelect label="Customer" value={f.customer}
            onChange={v => { setPage(1); setF({ ...f, customer: v }); }}
            options={data?.facets?.customers} />
          <FSelect label="Status" value={f.status}
            onChange={v => { setPage(1); setF({ ...f, status: v }); }}
            options={data?.facets?.statuses} />
          <FSelect label="Assigned User" value={f.assigned_user}
            onChange={v => { setPage(1); setF({ ...f, assigned_user: v }); }}
            options={data?.facets?.assigned_users} />
          <FSelect label="Currency" value={f.currency}
            onChange={v => { setPage(1); setF({ ...f, currency: v }); }}
            options={data?.facets?.currencies} />
          <FSelect label="Source" value={f.source}
            onChange={v => { setPage(1); setF({ ...f, source: v }); }}
            options={data?.facets?.sources} />
          <FSelect label="Brand" value={f.brand}
            onChange={v => { setPage(1); setF({ ...f, brand: v }); }}
            options={data?.facets?.brands} />
          <FDate label="From" value={f.date_from}
            onChange={v => { setPage(1); setF({ ...f, date_from: v }); }} />
          <FDate label="To" value={f.date_to}
            onChange={v => { setPage(1); setF({ ...f, date_to: v }); }} />
          <div className="col-span-2 md:col-span-4 relative">
            <Search className="h-3 w-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Free text — quote #, subject, customer…"
              value={f.q} onChange={e => { setPage(1); setF({ ...f, q: e.target.value }); }}
              className="h-8 pl-7 text-[12px]" data-testid="filter-q" />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-[11px]" data-testid="quotes-table">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
              <tr>
                <th className="p-2 text-left">Quote #</th>
                <th className="p-2 text-left">Customer</th>
                <th className="p-2 text-left">Subject</th>
                <th className="p-2 text-left">Quote Date</th>
                <th className="p-2 text-left">Valid Until</th>
                <th className="p-2 text-right">Value</th>
                <th className="p-2 text-left">Currency</th>
                <th className="p-2 text-left">Status</th>
                <th className="p-2 text-left">Assigned</th>
                <th className="p-2 text-left">Source</th>
                <th className="p-2 text-left">Last Synced</th>
              </tr>
            </thead>
            <tbody>
              {(data?.items || []).map(q => (
                <tr key={q.id}
                  onClick={() => setOpenId(q.id)}
                  className="border-t border-slate-100 hover:bg-emerald-50/40 cursor-pointer"
                  data-testid={`quote-row-${q.id}`}>
                  <td className="p-2 font-mono font-semibold text-emerald-800">{q.quote_number || "—"}</td>
                  <td className="p-2 text-slate-800">{q.account_name || <span className="text-amber-700 font-mono text-[10px]">unresolved · {q.account_id}</span>}</td>
                  <td className="p-2 text-slate-700 max-w-[260px] truncate" title={q.subject}>{q.subject || "—"}</td>
                  <td className="p-2 text-slate-500 text-[10px]">{q.quote_date || "—"}</td>
                  <td className="p-2 text-slate-500 text-[10px]">{q.valid_until || "—"}</td>
                  <td className="p-2 text-right font-mono font-semibold">{fmt(q.grand_total)}</td>
                  <td className="p-2 font-mono text-slate-600">{q.currency_id || "—"}</td>
                  <td className="p-2">
                    <Badge className={"text-[10px] " + (STATUS_TONE[q.status] || "")}>{q.status}</Badge>
                  </td>
                  <td className="p-2 font-mono text-slate-500 text-[10px]">{q.assigned_user_id || "—"}</td>
                  <td className="p-2">
                    <Badge className={"text-[10px] " + (SYNC_TONE[q.vtiger_sync_state] || "bg-slate-100 border-slate-200 text-slate-600")}>
                      {q.source || "—"}
                    </Badge>
                  </td>
                  <td className="p-2 text-slate-500 text-[10px]">
                    {q.last_synced_at ? new Date(q.last_synced_at).toLocaleString() : "—"}
                  </td>
                </tr>
              ))}
              {!(data?.items || []).length && !busy && (
                <tr><td colSpan={11} className="p-8 text-center text-slate-400">
                  No quotations match the current filter.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-3 py-2 border-t border-slate-200 text-[11px]">
          <div className="text-slate-500">
            Showing {(data?.items || []).length} of {(data?.total ?? 0).toLocaleString()}
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}>Prev</Button>
            <span className="text-slate-600">Page {page}</span>
            <Button size="sm" variant="outline"
              disabled={(data?.items || []).length < 25}
              onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        </div>
      </div>

      {openId && <Quote360 quoteId={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}


/* ─── Quote 360 drawer ─────────────────────────────────────────── */
function Quote360({ quoteId, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/commercial/quotations/quote-360/${quoteId}`);
        setD(res.data);
      } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    })();
  }, [quoteId]);

  const q = d?.quote;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-start justify-end overflow-y-auto"
      data-testid="quote-360-drawer" onClick={onClose}>
      <div className="bg-white shadow-2xl w-full max-w-4xl min-h-screen overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <Button size="sm" variant="outline" onClick={onClose} data-testid="q360-close">
            <ArrowLeft className="h-3 w-3 mr-1" /> Close
          </Button>
          <div className="text-sm font-heading font-semibold">Quote 360</div>
        </div>
        {!q ? (
          <div className="p-8 text-center text-slate-500 text-[12px]">Loading…</div>
        ) : (
          <div className="p-4 space-y-4">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Overview</div>
              <div className="text-lg font-heading font-semibold text-slate-900">{q.quote_number}</div>
              <div className="text-[12px] text-slate-700">{q.subject}</div>
              <div className="text-[11px] text-slate-500 mt-1">
                {q.quote_date} · valid until {q.valid_until || "—"} · {q.currency_id}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <Badge className={"text-[10px] " + (STATUS_TONE[q.status] || "")}>{q.status}</Badge>
                <Badge className={"text-[10px] " + (SYNC_TONE[q.vtiger_sync_state] || "")}>{q.vtiger_sync_state}</Badge>
              </div>
            </div>
            <Sec title="Customer">
              {d?.customer ? (
                <>
                  <Kv k="Account" v={d.customer.payload?.accountname} />
                  <Kv k="Website" v={d.customer.payload?.website} />
                  <Kv k="Phone"   v={d.customer.payload?.phone} />
                  <Kv k="Email"   v={d.customer.payload?.email1} />
                  <Kv k="Legacy import ID" v={d.customer.vtiger_id} />
                </>
              ) : <div className="text-[11px] text-slate-500">Customer reference unresolved · {q.account_id}</div>}
            </Sec>
            <Sec title={`Line Items (${(q.line_items || []).length})`}>
              {(q.line_items || []).length === 0 ? (
                <div className="text-[11px] text-slate-500">
                  Legacy import carried the header only — no line items were imported for this quotation.
                </div>
              ) : (
                <table className="text-[11px] w-full">
                  <thead className="text-slate-500 uppercase text-[10px]"><tr>
                    <th className="p-1 text-left">Product</th><th>Qty</th><th>Unit</th><th>Disc</th><th>Tax</th><th className="text-right">Line Total</th>
                  </tr></thead>
                  <tbody>
                    {(q.line_items || []).map((li, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="p-1">{li.product_name || li.product_id}</td>
                        <td className="p-1 text-right font-mono">{li.quantity}</td>
                        <td className="p-1 text-right font-mono">{li.unit_price}</td>
                        <td className="p-1 text-right font-mono">{li.discount}</td>
                        <td className="p-1 text-right font-mono">{li.tax}</td>
                        <td className="p-1 text-right font-mono">{li.line_total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Sec>
            <Sec title="Commercial Summary">
              <Kv k="Subtotal" v={q.subtotal} />
              <Kv k="Discount" v={q.discount} />
              <Kv k="Tax"       v={q.tax} />
              <Kv k="Grand Total" v={q.grand_total} />
            </Sec>
            <Sec title="Terms"><div className="text-[11px] whitespace-pre-wrap">{q.terms || "—"}</div></Sec>
            <Sec title="Description"><div className="text-[11px] whitespace-pre-wrap">{q.description || "—"}</div></Sec>
            <Sec title={`Related Customer PO (${(d?.customer_pos || []).length})`}>
              {(d?.customer_pos || []).map(p => <div key={p.id} className="text-[11px] border-b border-slate-100 py-1">
                <span className="font-mono">{p.code}</span> · {p.status} · {p.grand_total}
              </div>)}
              {!(d?.customer_pos || []).length && <div className="text-[11px] text-slate-400">None</div>}
            </Sec>
            <Sec title={`Related Orders (${(d?.orders || []).length})`}>
              {!(d?.orders || []).length && <div className="text-[11px] text-slate-400">None</div>}
            </Sec>
            <Sec title={`Related Invoices (${(d?.invoices || []).length})`}>
              {!(d?.invoices || []).length && <div className="text-[11px] text-slate-400">None</div>}
            </Sec>
            <Sec title={`Documents (${(d?.documents || []).length})`}>
              {!(d?.documents || []).length && <div className="text-[11px] text-slate-400">None</div>}
            </Sec>
            <Sec title={`Activity (${(d?.activity || []).length})`}>
              {(d?.activity || []).slice(0, 8).map(a => <div key={a.id} className="text-[11px] border-b border-slate-100 py-1">
                <span className="font-mono text-emerald-700">{a.action}</span> · {a.at} · {a.actor}
              </div>)}
              {!(d?.activity || []).length && <div className="text-[11px] text-slate-400">None</div>}
            </Sec>
            <Sec title="Source Information">
              <Kv k="Source"     v={/vtiger/i.test(d?.source?.source || "") ? "Legacy import" : d?.source?.source} />
              <Kv k="Legacy import ID" v={d?.source?.vtiger_id} />
              <Kv k="Legacy modified" v={d?.source?.vtiger_modified_time} />
              <Kv k="Imported" v={d?.source?.last_synced_at} />
            </Sec>
            <Sec title={`History (${(d?.history || []).length})`}>
              {!(d?.history || []).length && <div className="text-[11px] text-slate-400">None</div>}
            </Sec>
          </div>
        )}
      </div>
    </div>
  );
}


function Kpi({ label, value, tone, large, tiny }) {
  const tint = tone === "ok"   ? "text-emerald-800 bg-emerald-50 border-emerald-200"
             : tone === "warn" ? "text-amber-800 bg-amber-50 border-amber-200"
             : tone === "err"  ? "text-rose-800 bg-rose-50 border-rose-200"
             : tone === "sky"  ? "text-sky-800 bg-sky-50 border-sky-200"
             : "text-slate-700 bg-white border-slate-200";
  return (
    <div className={"border rounded-lg p-3 " + tint}>
      <div className={"uppercase tracking-wider " + (tiny ? "text-[9px]" : "text-[10px]")}>{label}</div>
      <div className={"font-heading font-semibold mt-0.5 truncate "
        + (large ? "text-[24px]" : tiny ? "text-[12px]" : "text-[20px]")}>
        {value ?? "—"}
      </div>
    </div>
  );
}


function Sec({ title, children }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">{title}</div>
      <div>{children}</div>
    </div>
  );
}


function Kv({ k, v }) {
  return (
    <div className="text-[11px] grid grid-cols-3 py-0.5">
      <div className="text-slate-500">{k}</div>
      <div className="col-span-2 text-slate-800">{v == null || v === "" ? "—" : String(v)}</div>
    </div>
  );
}


function FSelect({ label, value, onChange, options }) {
  return (
    <label className="block">
      <div className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</div>
      <select value={value} onChange={e => onChange(e.target.value)}
        className="h-8 w-full border border-slate-200 rounded px-1.5 text-[12px] mt-0.5"
        data-testid={`filter-${label.toLowerCase().replace(/\s+/g, '-')}`}>
        <option value="">— any —</option>
        {(options || []).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}


function FDate({ label, value, onChange }) {
  return (
    <label className="block">
      <div className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</div>
      <input type="date" value={value} onChange={e => onChange(e.target.value)}
        className="h-8 w-full border border-slate-200 rounded px-1.5 text-[12px] mt-0.5" />
    </label>
  );
}
